import React, { useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AlertCircle, Loader2, User, Lock } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';

// Site key publica do reCAPTCHA v2 (definida no build via env var).
// Se vazia (dev local), o widget nao e exibido e o backend nao valida.
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

// Ícone da estrela do Kronex (mantido igual ao seu, mas com um container melhor)
const KronexLogo = () => (
  <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="40" height="40" rx="10" fill="white" fillOpacity="0.2" />
    <path
      d="M20 8L24.5 16H30L25.5 21.5L27.5 29.5L20 25L12.5 29.5L14.5 21.5L10 16H15.5L20 8Z"
      fill="white"
      fillOpacity="0.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20 13L22.8 18.5H27L23.5 22L24.8 27L20 24.2L15.2 27L16.5 22L13 18.5H17.2L20 13Z"
      fill="white"
    />
  </svg>
);

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  // Com captcha habilitado, o botao so libera apos resolver o desafio
  const captchaPending = !!RECAPTCHA_SITE_KEY && !captchaToken;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password.trim()) {
      setError('Por favor, preencha usuário e senha.');
      return;
    }

    if (captchaPending) {
      setError('Confirme o reCAPTCHA "Não sou um robô".');
      return;
    }

    setLoading(true);
    const result = await login(username, password, captchaToken);
    setLoading(false);

    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
      // O token do reCAPTCHA e de uso unico: reseta o widget para nova tentativa
      if (RECAPTCHA_SITE_KEY && captchaRef.current) {
        captchaRef.current.reset();
        setCaptchaToken(null);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl shadow-blue-900/10 overflow-hidden border border-gray-100/50">
          
          {/* Header */}
          <div className="bg-[#1e3a5f] px-8 py-8 text-center relative overflow-hidden">
            {/* Decorative blurs (opcional, para dar um ar moderno) */}
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-500/20 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-indigo-500/20 rounded-full blur-xl"></div>
            
            <div className="flex justify-center mb-3 relative z-10">
              <div className="p-2.5 bg-white/10 rounded-2xl backdrop-blur-sm ring-1 ring-white/20 shadow-lg shadow-black/10">
                <KronexLogo />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight relative z-10">Kronex</h1>
            <p className="text-blue-200/80 text-sm mt-1.5 font-medium tracking-wide relative z-10">
              Gestão de Consultores
            </p>
          </div>

          {/* Form */}
          <div className="p-8 sm:p-10 pt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Acesse sua conta</h2>
            </div>

            {/* Alerta de Erro */}
            {error && (
              <div className="mb-5 flex items-start gap-3 bg-red-50/80 border-l-4 border-red-500 p-4 rounded-r-lg text-sm text-red-700 shadow-sm">
                <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Usuário
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Digite seu usuário"
                    autoComplete="username"
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 focus:bg-white transition-all"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Digite sua senha"
                    autoComplete="current-password"
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 focus:bg-white transition-all"
                    disabled={loading}
                  />
                </div>
                {/* Link "Esqueceu a senha?" (Opcional) */}
                {/* <div className="flex justify-end mt-2">
                  <a href="#" className="text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors font-medium">
                    Esqueceu a senha?
                  </a>
                </div> */}
              </div>

              {RECAPTCHA_SITE_KEY && (
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={captchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(token) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken(null)}
                    hl="pt-BR"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading || captchaPending}
                className="w-full py-3.5 px-4 bg-[#1e3a5f] hover:bg-[#162d49] active:bg-[#12253d] text-white font-semibold rounded-xl shadow-md shadow-blue-900/20 hover:shadow-lg hover:shadow-blue-900/30 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Entrando...
                  </>
                ) : (
                  'Entrar'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/esqueci-senha"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors font-medium"
              >
                Esqueci minha senha
              </Link>
            </div>
          </div>
        </div>

        <p className="text-center text-gray-400 text-xs mt-6 tracking-wide">
          © {new Date().getFullYear()} Kronex. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
};

export default Login;